import React, { useState, useContext, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Forward, Reply, Trash2, Smile, Pencil, Loader2, Plus } from "lucide-react";

import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import ForwardMessageModal from "../ForwardMessageModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { useChatOptimistic } from "../../context/ChatOptimistic/ChatOptimisticContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const RAPIDOS = [
	"👍", "❤️", "😂", "😮", "😢", "🙏", "🔥", "🎉",
	"😍", "🤔", "👏", "😅", "✅", "💯"
];

const RAPIDOS_POR_LINHA = 7;

const LARGURA_MENU = 220;
const MARGEM_VIEWPORT = 8;
const GAP = 4;

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl, anchorRect }) => {
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const { atualizarMensagem } = useChatOptimistic();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [encaminhar, setEncaminhar] = useState(false);
	const [editando, setEditando] = useState(false);
	const [textoEditado, setTextoEditado] = useState("");
	const [salvandoEdicao, setSalvandoEdicao] = useState(false);
	const [pickerAberto, setPickerAberto] = useState(false);
	const menuRef = useRef(null);
	const wrapperRef = useRef(null);
	const [posicao, setPosicao] = useState({ top: 0, left: 0, pronto: false, invertido: false });

	useEffect(() => {
		if (!menuOpen) return undefined;

		const aoClicarFora = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				handleClose();
			}
		};
		const aoTeclar = (e) => e.key === "Escape" && handleClose();

		document.addEventListener("mousedown", aoClicarFora);
		document.addEventListener("keydown", aoTeclar);

		return () => {
			document.removeEventListener("mousedown", aoClicarFora);
			document.removeEventListener("keydown", aoTeclar);
		};
	}, [menuOpen, handleClose]);

	const MINUTOS_PARA_EDITAR = 15;
	const [dentroDoPrazo, setDentroDoPrazo] = useState(false);

	useEffect(() => {
		if (!menuOpen) return undefined;

		const conferir = setTimeout(() => {
			const minutos =
				(Date.now() - new Date(message.createdAt).getTime()) / 1000 / 60;

			setDentroDoPrazo(minutos < MINUTOS_PARA_EDITAR);
		}, 0);

		return () => clearTimeout(conferir);
	}, [menuOpen, message.createdAt]);

	const podeEditar =
		message.fromMe && !message.isDeleted && !message.mediaUrl && dentroDoPrazo;

	const salvarEdicao = async () => {
		const texto = textoEditado.trim();

		if (!texto || texto === message.body) {
			setEditando(false);
			return;
		}

		setSalvandoEdicao(true);

		try {
			const { data: mensagemAtualizada } = await api.put(`/messages/${message.id}`, { body: texto });
			atualizarMensagem(message.id, {
				...(mensagemAtualizada || {}),
				id: mensagemAtualizada?.id ?? message.id,
				body: (mensagemAtualizada?.body && mensagemAtualizada.body !== message.body)
					? mensagemAtualizada.body
					: texto,
				editedAt: mensagemAtualizada?.editedAt ?? new Date().toISOString(),
				previousBody: mensagemAtualizada?.previousBody ?? message.body,
			}, { marcarComoConfirmada: true });
			toast.success("Mensagem editada.");
			setEditando(false);
		} catch (err) {
			toastError(err);
		}

		setSalvandoEdicao(false);
	};

	const apagarMensagem = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
			atualizarMensagem(message.id, {
				isDeleted: true,
				deletedAt: new Date().toISOString(),
				id: message.id,
			}, { marcarComoConfirmada: true });
		} catch (err) {
			toastError(err);
		} finally {
			setConfirmationOpen(false);
		}
	};

	const responder = () => {
		setReplyingMessage(message);
		handleClose();
	};

	const reagir = async (emoji) => {
		handleClose();

		try {
			await api.post(`/messages/${message.id}/reaction`, { emoji });
		} catch (err) {
			toastError(err);
		}
	};

	const salvarFigurinha = async () => {
		handleClose();

		try {
			await api.post(`/figurinhas/da-mensagem/${message.id}`, {});
			toast.success("Figurinha salva. Ela já está no painel de figurinhas.");
		} catch (err) {
			toastError(err);
		}
	};

	const abrirConfirmacao = () => {
		setConfirmationOpen(true);
		handleClose();
	};

	const rect =
		anchorRect &&
		Number.isFinite(anchorRect.right) &&
		anchorRect.right > 2 &&
		anchorRect.bottom > 2
			? anchorRect
			: (() => {
					const r = anchorEl?.getBoundingClientRect();
					return r && r.right > 2 && r.bottom > 2 ? r : null;
				})();

	const vh = typeof window !== "undefined" ? window.innerHeight : 800;
	const vw = typeof window !== "undefined" ? window.innerWidth : 1280;

	// ── Posição FINAL: colada no balão. NÃO usa altura estimada mais. ──
	//
	// Primeiro pintamos o menu "offscreen" (sem transition e em coordenadas
	// neutras) para medir a ALTURA REAL (varia conforme tem/sem editar,
	// tem/sem figurinha). No próximo frame recalculamos:
	//   • se couber ABAIXO do botão: abre ATRIBALHADO nele (rect.bottom + 4).
	//   • senão: abre ACIMA, colado no TOPO do balão (rect.top - altura - 4).
	//
	// Resultado: nunca fica "muito pra cima" nem "muito pra baixo".
	useLayoutEffect(() => {
		if (!menuOpen || !rect) {
			setPosicao({ top: 0, left: 0, pronto: false, invertido: false });
			return undefined;
		}

		if (!wrapperRef.current) return undefined;

		// Reseta posição para o cálculo não acumular tamanhos.
		setPosicao({ top: 0, left: 0, pronto: false, invertido: false });

		let raf1, raf2;
		raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(() => {
				const el = wrapperRef.current;
				if (!el) return;
				const h = el.offsetHeight;
				const w = el.offsetWidth || LARGURA_MENU;

				// Horizontal: sempre alinha a BORDA DIREITA do menu com a do balão.
				// Nunca sai da viewport pelas laterais (clamp 8px min/max).
				let leftIdeal = rect.right - w;
				leftIdeal = Math.max(
					MARGEM_VIEWPORT,
					Math.min(vw - w - MARGEM_VIEWPORT, leftIdeal)
				);

				const cabeAbaixo = rect.bottom + GAP + h <= vh - MARGEM_VIEWPORT;
				let top;
				let invertido;
				if (cabeAbaixo) {
					top = rect.bottom + GAP;
					invertido = false;
				} else {
					// Caso não caiba nem para cima (tela minúscula), prioriza ficar
					// 8px abaixo do topo em vez de cortar.
					const topInvertido = rect.top - h - GAP;
					top = Math.max(MARGEM_VIEWPORT, topInvertido);
					invertido = true;
				}
				setPosicao({ top, left: leftIdeal, pronto: true, invertido });
			});
		});
		return () => {
			if (raf1) cancelAnimationFrame(raf1);
			if (raf2) cancelAnimationFrame(raf2);
		};
	}, [menuOpen, rect && rect.top, rect && rect.bottom, rect && rect.right, rect && rect.left]);

	const leftFixup = useMemo(() => {
		// Enquanto não mediu, já dá um chute horizontal pra ficar alinhado
		// grosseiramente com a borda direita e não aparecer 1 frame no x=0.
		if (!rect) return 0;
		return Math.max(
			MARGEM_VIEWPORT,
			Math.min(vw - LARGURA_MENU - MARGEM_VIEWPORT, rect.right - LARGURA_MENU)
		);
	}, [rect, vw]);

	return (
		<>
			<ForwardMessageModal
				open={encaminhar}
				onClose={() => setEncaminhar(false)}
				message={message}
			/>
			<Dialog open={editando} onOpenChange={(o) => !o && setEditando(false)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Editar mensagem</DialogTitle>
					</DialogHeader>

					<textarea
						value={textoEditado}
						onChange={(e) => setTextoEditado(e.target.value)}
						rows={4}
						autoFocus
						disabled={salvandoEdicao}
						className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
					/>

					<p className="text-xs text-muted-foreground">
						O cliente vê a mensagem corrigida com a marca de editada. O texto
						anterior fica guardado no histórico do atendimento.
					</p>

					<div className="flex justify-end gap-2">
						<Button
							variant="ghost"
							onClick={() => setEditando(false)}
							disabled={salvandoEdicao}
						>
							Cancelar
						</Button>
						<Button
							onClick={salvarEdicao}
							disabled={salvandoEdicao || !textoEditado.trim()}
							className="gap-2"
						>
							{salvandoEdicao && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={apagarMensagem}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			<Dialog
				open={pickerAberto}
				onOpenChange={(o) => {
					setPickerAberto(o);
					if (!o) handleClose();
				}}
			>
				<DialogContent className="sm:max-w-md border-0 bg-transparent p-0 shadow-none">
					<DialogHeader className="sr-only">
						<DialogTitle>Reagir com emoji</DialogTitle>
					</DialogHeader>
					<div className="rounded-lg border bg-popover shadow-xl">
						<Picker
							data={emojiData}
							locale="pt"
							perLine={8}
							previewPosition="none"
							skinTonePosition="search"
							maxFrequentRows={2}
							onEmojiSelect={(e) => {
								if (!e?.native) return;
								reagir(e.native);
								setPickerAberto(false);
							}}
						/>
					</div>
				</DialogContent>
			</Dialog>

			{menuOpen &&
				rect &&
				createPortal(
					<div
						ref={(el) => {
							wrapperRef.current = el;
							menuRef.current = el;
						}}
						role="menu"
						style={{
							position: "fixed",
							top: posicao.pronto ? posicao.top : -9999,
							left: posicao.pronto ? posicao.left : leftFixup,
							visibility: posicao.pronto ? "visible" : "hidden",
							transformOrigin: posicao.invertido ? "bottom right" : "top right",
						}}
						className="z-50 w-[220px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in zoom-in-95"
					>
						{!message.isDeleted && (
							<div className="mb-1 border-b px-1 pb-1.5">
								{Array.from(
									{ length: Math.ceil(RAPIDOS.length / RAPIDOS_POR_LINHA) },
									(_, i) => (
										<div
											key={i}
											className={`flex items-center justify-between ${i > 0 ? "mt-1" : ""}`}
										>
											{RAPIDOS
												.slice(i * RAPIDOS_POR_LINHA, (i + 1) * RAPIDOS_POR_LINHA)
												.map((emoji) => (
													<button
														key={emoji}
														type="button"
														title={`Reagir com ${emoji}`}
														onClick={() => reagir(emoji)}
														className="rounded-full px-1 text-lg leading-none transition-transform hover:scale-125"
													>
														{emoji}
													</button>
												))}
										</div>
									)
								)}
								<div className="mt-2 flex items-center gap-1 border-t pt-1.5">
									<button
										type="button"
										title="Mais emojis…"
										onClick={() => {
											setPickerAberto(true);
										}}
										className="flex flex-1 items-center justify-center gap-1 rounded-sm py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
									>
										<Plus className="h-3.5 w-3.5" />
										Mais emojis
									</button>
								</div>
							</div>
						)}

						<button
							type="button"
							role="menuitem"
							onClick={responder}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Reply className="h-4 w-4" />
							{i18n.t("messageOptionsMenu.reply")}
						</button>

						<button
							type="button"
							role="menuitem"
							onClick={() => {
								setEncaminhar(true);
								handleClose();
							}}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Forward className="h-4 w-4" />
							Encaminhar
						</button>

						{message.mediaType === "sticker" && (
							<button
								type="button"
								role="menuitem"
								onClick={salvarFigurinha}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Smile className="h-4 w-4" />
								Salvar figurinha
							</button>
						)}

						{podeEditar && (
							<button
								type="button"
								role="menuitem"
								onClick={() => {
									setEditando(true);
									setTextoEditado(message.body || "");
									handleClose();
								}}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Pencil className="h-4 w-4" />
								Editar
							</button>
						)}

						{message.fromMe && (
							<button
								type="button"
								role="menuitem"
								onClick={abrirConfirmacao}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
							>
								<Trash2 className="h-4 w-4" />
								Apagar para todos
							</button>
						)}
					</div>,
					document.body
				)}
		</>
	);
};

export default MessageOptionsMenu;
